import React, { useContext, useEffect, useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  Icon,
  Zoom,
  useTheme,
} from '@mui/material';
import AlertaContext from '../context/AlertaContext';

const Alerta = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { stateAlerta, handleCloseAlerta } = useContext(AlertaContext);
  const { open, tipo, mensaje } = stateAlerta;
  const [progress, setProgress] = useState(100);

  const getIconAndColor = (type) => {
    switch (type) {
      case 'error':
        return {
          icon: 'cancel',
          color: '#EF4444',
          bgLight: isDark ? 'rgba(239, 68, 68, 0.18)' : 'rgba(239, 68, 68, 0.08)',
          titulo: 'Algo salió mal',
        };
      case 'warning':
        return {
          icon: 'warning',
          color: '#F59E0B',
          bgLight: isDark ? 'rgba(245, 158, 11, 0.18)' : 'rgba(245, 158, 11, 0.08)',
          titulo: 'Atención, revisa esto',
        };
      case 'info':
        return {
          icon: 'info',
          color: '#3B82F6',
          bgLight: isDark ? 'rgba(59, 130, 246, 0.18)' : 'rgba(59, 130, 246, 0.08)',
          titulo: 'Mira esto',
        };
      case 'success':
      default:
        return {
          icon: 'check_circle',
          color: '#10B981',
          bgLight: isDark ? 'rgba(16, 185, 129, 0.18)' : 'rgba(16, 185, 129, 0.08)',
          titulo: '¡Todo salió bien!',
        };
    }
  };

  const { icon, color, bgLight, titulo } = getIconAndColor(tipo);

  useEffect(() => {
    if (open) {
      setProgress(100);
      const startTime = Date.now();
      const duration = 4000;

      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
        setProgress(remaining);
        if (remaining <= 0) {
          clearInterval(interval);
        }
      }, 50);

      const timer = setTimeout(() => {
        handleCloseAlerta();
      }, duration);

      return () => {
        clearInterval(interval);
        clearTimeout(timer);
      };
    }
  }, [open, handleCloseAlerta]);

  return (
    <Dialog
      open={Boolean(open)}
      onClose={handleCloseAlerta}
      TransitionComponent={Zoom}
      transitionDuration={250}
      PaperProps={{
        sx: {
          borderRadius: '24px',
          pt: 4,
          pb: 2.5,
          px: { xs: 3, sm: 4 },
          maxWidth: 380,
          width: '90%',
          textAlign: 'center',
          boxShadow: isDark
            ? '0 20px 60px rgba(0, 0, 0, 0.6)'
            : '0 20px 60px rgba(15, 23, 42, 0.25)',
          overflow: 'hidden',
          bgcolor: isDark ? '#131b2e' : '#ffffff',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : 'none',
          position: 'relative',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? 'rgba(0, 0, 0, 0.65)' : 'rgba(15, 23, 42, 0.45)',
          backdropFilter: 'blur(3px)',
        },
      }}
    >
      {/* Círculo con Ícono Central */}
      <Box
        sx={{
          width: 88,
          height: 88,
          borderRadius: '50%',
          bgcolor: bgLight,
          color: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2.2,
          transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
          transform: open ? 'scale(1)' : 'scale(0.5)',
        }}
      >
        <Icon sx={{ fontSize: 48, color: color }}>{icon}</Icon>
      </Box>

      {/* Título */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 900,
          color: color,
          fontSize: '1.2rem',
          mb: 1,
          letterSpacing: '-0.01em',
        }}
      >
        {titulo}
      </Typography>

      {/* Mensaje */}
      <Typography
        variant="body2"
        sx={{
          color: isDark ? '#94a3b8' : '#64748b',
          fontSize: '0.92rem',
          lineHeight: 1.6,
          mb: 2.5,
          px: 1,
        }}
      >
        {mensaje}
      </Typography>

      {/* Barra de progreso de auto-cierre */}
      <Box
        sx={{
          width: '100%',
          height: 3,
          borderRadius: 2,
          bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f1f5f9',
          mb: 2.5,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            bgcolor: color,
            width: `${progress}%`,
            transition: 'width 0.05s linear',
          }}
        />
      </Box>

      {/* Botón de Confirmación OK Outlined */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          onClick={handleCloseAlerta}
          variant="outlined"
          sx={{
            py: 1,
            px: 5,
            borderRadius: '12px',
            borderWidth: '1.5px',
            borderColor: `${color}40`,
            color: color,
            fontWeight: 900,
            fontSize: '0.95rem',
            textTransform: 'none',
            bgcolor: isDark ? '#1e293b' : '#ffffff',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderWidth: '1.5px',
              borderColor: color,
              bgcolor: bgLight,
            },
          }}
        >
          OK
        </Button>
      </Box>
    </Dialog>
  );
};

export default Alerta;
